import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ChevronRight, ChevronLeft, Star, Ban, FileText, Paperclip, X } from 'lucide-react';
import { format } from 'date-fns';
import { tendersAPI, bidAgentsAPI } from '../services/api';
import { logEvent } from '../utils/eventLog';
import { useBookmarks } from '../contexts/BookmarkContext';
import { useTenderReview } from '../contexts/TenderReviewContext';
import { useDisqualifications } from '../contexts/DisqualificationContext';
import DateRangeFilter from '../components/DateRangeFilter';
import './Dashboard.css';

const AUTO_DISQUALIFY_CATEGORIES = ['construction', 'services related to goods'];
const VEHICLE_KEYWORDS = [
  'vehicle', 'vehicles', 'truck', 'trucks', 'trailer', 'trailers',
  'automobile', 'automotive', 'fleet', 'bus ', 'buses', 'van ', 'vans',
  'pickup', 'sedan', 'suv', 'aircraft', 'helicopter', 'vessel', 'boat',
  'locomotive', 'snowplow', 'forklift', 'excavator', 'bulldozer',
];

const isAutoDisqualifiedCategory = (tender) => {
  const cat = (
    tender['masterCategory-catégoriePrincipale'] ||
    tender.procurementCategory ||
    tender.procurement_category ||
    ''
  ).toLowerCase();
  if (AUTO_DISQUALIFY_CATEGORIES.some((kw) => cat.includes(kw))) return true;

  const title = (tender.title || tender['title-titre-eng'] || '').toLowerCase();
  if (VEHICLE_KEYWORDS.some((kw) => title.includes(kw))) return true;

  return false;
};

const Recommended = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tenders, setTenders] = useState([]);
  const [totalTenders, setTotalTenders] = useState(0);
  const [loading, setLoading] = useState(true);
  const [maxScore, setMaxScore] = useState(0);
  const [screenResults, setScreenResults] = useState({});
  const [screening, setScreening] = useState(false);

  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('size')) || 25);
  const [searchType, setSearchType] = useState(searchParams.get('type') || 'hybrid');
  const [hasPdfsFilter, setHasPdfsFilter] = useState(searchParams.get('hasPdfs') === 'true');
  const [sortBy, setSortBy] = useState(searchParams.get('sort_by') || 'relevance');

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const { disqualify: bulkDisqualifyOne } = useDisqualifications();
  const toggleSelected = (tid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(tid) ? next.delete(tid) : next.add(tid);
      return next;
    });
  };
  const exitSelectionMode = () => { setSelectionMode(false); setSelectedIds(new Set()); };
  const bulkDisqualifySelected = async () => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    const targets = tenders.filter(
      (t) => selectedIds.has(t._id || t.tender_id || t.reference_number),
    );
    try {
      for (const t of targets) {
        try {
          await bulkDisqualifyOne({
            tender_id: t._id || t.tender_id || t.reference_number,
            title: t.title,
            organization: typeof t.organization === 'object'
              ? (t.organization?.contracting_entity || t.organization?.name)
              : t.organization,
            closing_date: t.closing_date || t.closingDate,
          });
        } catch (e) {
          console.warn('bulk disqualify (one) failed', t._id || t.tender_id, e);
        }
      }
    } finally {
      setBulkBusy(false);
      exitSelectionMode();
    }
  };

  const fromDate = searchParams.get('from_date') || '';
  const toDate = searchParams.get('to_date') || '';
  const closingFrom = searchParams.get('closing_from') || '';
  const closingTo = searchParams.get('closing_to') || '';

  const updateUrlParams = (updates) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== '') {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    fetchTenders();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchTenders();
  }, [currentPage, searchTerm, searchType, pageSize, hasPdfsFilter, sortBy, fromDate, toDate, closingFrom, closingTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTenders = async () => {
    setLoading(true);
    try {
      const filters = { categories: ['Goods'] };
      if (hasPdfsFilter) filters.has_pdfs = true;
      if (fromDate) filters.from_date = fromDate;
      if (toDate) filters.to_date = toDate;
      if (closingFrom) filters.closing_date_from = closingFrom;
      if (closingTo) filters.closing_date_to = closingTo;

      const searchRequestParams = {
        page: currentPage,
        size: pageSize,
        sort_by: sortBy,
        filters
      };

      let response;

      if (searchType === 'semantic' && searchTerm.trim()) {
        response = await tendersAPI.semanticSearchTenders({
          query: searchTerm,
          ...searchRequestParams
        });
        setMaxScore(response.max_score || 0);
      } else if (searchType === 'keyword' && searchTerm.trim()) {
        response = await tendersAPI.keywordSearchTenders({
          query: searchTerm,
          ...searchRequestParams
        });
        setMaxScore(response.max_score || 0);
      } else if (searchType === 'tender_id' && searchTerm.trim()) {
        try {
          const tender = await tendersAPI.getTender(searchTerm.trim());
          response = { tenders: [tender], total: 1 };
        } catch (error) {
          response = await tendersAPI.searchTenders({
            search: searchTerm,
            ...searchRequestParams
          });
        }
        setMaxScore(0);
      } else {
        response = await tendersAPI.searchTenders({
          search: searchTerm,
          ...searchRequestParams
        });
        setMaxScore(0);
      }

      const fetched = response.tenders || [];
      setTenders(fetched);
      setTotalTenders(response.total || 0);

      // Screen for hard blockers asynchronously — don't block the render
      if (fetched.length > 0) {
        setScreening(true);
        bidAgentsAPI.screenTenders(fetched)
          .then((res) => setScreenResults((prev) => ({ ...prev, ...res.results })))
          .catch((err) => console.warn('Screening failed, showing all tenders:', err))
          .finally(() => setScreening(false));
      }
    } catch (error) {
      console.error('Error fetching tenders:', error);
      setTenders([]);
      setTotalTenders(0);
      setMaxScore(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newPage = 1;
    setCurrentPage(newPage);
    updateUrlParams({ q: searchTerm, page: newPage, type: searchType });
    logEvent('search.executed', {
      meta: {
        query: searchTerm,
        search_type: searchType,
        page_size: pageSize,
        has_filters: !!(fromDate || toDate || closingFrom || closingTo || hasPdfsFilter !== 'any'),
      },
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateUrlParams({ page });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    updateUrlParams({ type });
  };

  const handlePageSizeChange = (size) => {
    const newSize = parseInt(size);
    setPageSize(newSize);
    setCurrentPage(1);
    updateUrlParams({ size: newSize, page: 1 });
  };

  const { isBookmarked: isBookmarkedForFilter } = useBookmarks();
  const { isDisqualified: isDisqualifiedForFilter } = useDisqualifications();

  useEffect(() => {
    if (loading || !tenders.length) return;
    const toAutoDisqualify = tenders.filter((t) => {
      const tid = t._id || t.tender_id || t.reference_number;
      return isAutoDisqualifiedCategory(t) && !isDisqualifiedForFilter(tid);
    });
    if (!toAutoDisqualify.length) return;
    let cancelled = false;
    (async () => {
      for (const t of toAutoDisqualify) {
        if (cancelled) break;
        try {
          await bulkDisqualifyOne({
            tender_id: t._id || t.tender_id || t.reference_number,
            title: t.title,
            organization: typeof t.organization === 'object'
              ? (t.organization?.contracting_entity || t.organization?.name)
              : t.organization,
            closing_date: t.closing_date || t.closingDate,
            reason: `Auto: ${t['masterCategory-catégoriePrincipale'] || t.procurementCategory || t.procurement_category || 'blocked category'}`,
          });
        } catch (e) {
          console.warn('auto-disqualify failed', t._id || t.tender_id, e);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [tenders, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredTenders = tenders.filter((tender) => {
    const tid = tender._id || tender.tender_id || tender.reference_number;
    if (isBookmarkedForFilter(tid) || isDisqualifiedForFilter(tid)) return false;
    if (isAutoDisqualifiedCategory(tender)) return false;

    // Hide hard-blocked tenders once screen results arrive
    const screen = screenResults[tid];
    if (screen && screen.blocked) return false;

    // Always hide closed tenders — Recommended is open opportunities only
    const closingDate = tender.closing_date || tender.closingDate || tender.close_date;
    if (!closingDate) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const closeDate = new Date(closingDate);
    closeDate.setHours(0, 0, 0, 0);
    return closeDate >= today;
  });

  return (
    <div className="dashboard">
      <div className="dashboard-container">
        <div className="dashboard-content-full">
          <div className="search-controls">
            <div className="dq-page-header">
              <div className="dq-page-title-row">
                <h1 className="dq-page-title">Recommended</h1>
                <span className="dq-page-subtitle">Tenders matched to your profile</span>
              </div>
            </div>

            <form className="search-bar" onSubmit={handleSearch}>
              <Search className="search-icon" />
              <input
                type="search"
                placeholder="Search listings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button type="submit" className="stripe-button">
                Search
              </button>
            </form>

            {(fromDate || toDate || closingFrom || closingTo) && (
              <div className="active-filters-banner">
                <span>
                  Filtered:
                  {fromDate && ` Published from ${fromDate}`}
                  {toDate && ` Published to ${toDate}`}
                  {closingFrom && ` Closing from ${closingFrom}`}
                  {closingTo && ` Closing to ${closingTo}`}
                </span>
                <button
                  className="clear-filters-btn"
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.delete('from_date');
                    params.delete('to_date');
                    params.delete('closing_from');
                    params.delete('closing_to');
                    params.delete('sort_by');
                    setSearchParams(params, { replace: true });
                    setSortBy('relevance');
                  }}
                >
                  Clear filters
                </button>
              </div>
            )}

            <div className="results-info compact-filters">
              <span className="results-count">
                <strong>{filteredTenders.length}</strong> recommended tender{filteredTenders.length !== 1 ? 's' : ''} on this page
                {screening && <span className="screening-indicator"> · screening…</span>}
              </span>

              <div className="filter-chip" title="Search type">
                <Search size={14} />
                <select
                  aria-label="Search type"
                  className="filter-chip-select"
                  value={searchType}
                  onChange={(e) => handleSearchTypeChange(e.target.value)}
                >
                  <option value="hybrid">Hybrid</option>
                  <option value="keyword">Keyword</option>
                  <option value="semantic">AI / semantic</option>
                  <option value="tender_id">Tender ID</option>
                </select>
              </div>

              <div className="filter-chip" title="Results per page">
                <FileText size={14} />
                <select
                  aria-label="Results per page"
                  className="filter-chip-select"
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                >
                  <option value="25">25 / page</option>
                  <option value="50">50 / page</option>
                  <option value="100">100 / page</option>
                </select>
              </div>


              <div className="filter-chip" title="Documents">
                <Paperclip size={14} />
                <select
                  aria-label="Documents"
                  className="filter-chip-select"
                  value={hasPdfsFilter ? 'pdfs' : 'all'}
                  onChange={(e) => {
                    const wantPdfs = e.target.value === 'pdfs';
                    setHasPdfsFilter(wantPdfs);
                    setCurrentPage(1);
                    updateUrlParams({ hasPdfs: wantPdfs ? 'true' : '', page: 1 });
                  }}
                >
                  <option value="all">Any docs</option>
                  <option value="pdfs">Has PDFs</option>
                </select>
              </div>

              <DateRangeFilter
                fromDate={fromDate}
                toDate={toDate}
                closingFrom={closingFrom}
                closingTo={closingTo}
                onApply={({ from_date, to_date, closing_from, closing_to }) => {
                  setCurrentPage(1);
                  updateUrlParams({
                    from_date, to_date, closing_from, closing_to, page: '1',
                  });
                }}
              />

              {searchType === 'semantic' && searchTerm.trim() && maxScore > 0 && (
                <div className="relevance-indicator">
                  Match: {(maxScore * 100).toFixed(1)}%
                </div>
              )}

              {(hasPdfsFilter || fromDate || toDate || closingFrom || closingTo) && (
                <button
                  type="button"
                  className="filter-chip filter-chip-clear"
                  onClick={() => {
                    setHasPdfsFilter(false);
                    setCurrentPage(1);
                    updateUrlParams({
                      hasPdfs: '',
                      from_date: '',
                      to_date: '',
                      closing_from: '',
                      closing_to: '',
                      page: '1',
                    });
                  }}
                  title="Clear all filters"
                >
                  <X size={14} />
                  Clear all
                </button>
              )}
            </div>
          </div>

          <div className="bulk-toolbar">
            {!selectionMode ? (
              <button
                type="button"
                className="bulk-toolbar-btn"
                onClick={() => setSelectionMode(true)}
                disabled={loading || filteredTenders.length === 0}
              >
                ☑ Select tenders
              </button>
            ) : (
              <>
                <span className="bulk-toolbar-count">
                  {selectedIds.size} selected
                </span>
                <button
                  type="button"
                  className="bulk-toolbar-btn primary"
                  onClick={bulkDisqualifySelected}
                  disabled={selectedIds.size === 0 || bulkBusy}
                  title="Disqualify all selected tenders (team-wide)"
                >
                  <Ban size={12} /> {bulkBusy ? 'Disqualifying…' : `Disqualify ${selectedIds.size}`}
                </button>
                <button
                  type="button"
                  className="bulk-toolbar-btn"
                  onClick={() => setSelectedIds(new Set(filteredTenders.map(
                    (t) => t._id || t.tender_id || t.reference_number,
                  )))}
                  disabled={bulkBusy}
                >
                  Select all on page
                </button>
                <button
                  type="button"
                  className="bulk-toolbar-btn"
                  onClick={exitSelectionMode}
                  disabled={bulkBusy}
                >
                  Cancel
                </button>
              </>
            )}
          </div>

          {loading ? (
            <div className="loading">Loading tenders...</div>
          ) : (
            <>
              <div className="tenders-list">
                {filteredTenders.length === 0 && tenders.length > 0 ? (
                  <div className="no-results-message">
                    <p>No open recommended tenders on this page.</p>
                    <p>Try the next page.</p>
                  </div>
                ) : (
                  filteredTenders.map((tender) => {
                    const tid = tender._id || tender.tender_id || tender.reference_number;
                    return (
                      <TenderCard
                        key={tid}
                        tender={tender}
                        showRelevance={searchType === 'semantic' && searchTerm.trim()}
                        selectionMode={selectionMode}
                        selected={selectedIds.has(tid)}
                        onToggleSelect={toggleSelected}
                        canadianContentFlag={screenResults[tid]?.canadian_content_flag || false}
                      />
                    );
                  })
                )}
              </div>

              {totalTenders > pageSize && (
                <Pagination
                  currentPage={currentPage}
                  totalItems={totalTenders}
                  itemsPerPage={pageSize}
                  onPageChange={handlePageChange}
                  filteredCount={filteredTenders.length}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const TenderCard = ({ tender, showRelevance = false, selectionMode = false, selected = false, onToggleSelect = null, canadianContentFlag = false }) => {
  const {
    getTenderStatus,
    saveTender,
    unsaveTender,
    isSaved
  } = useTenderReview();
  const { addBookmark, removeBookmark, isBookmarked } = useBookmarks();
  const { isDisqualified, disqualify, undisqualify } = useDisqualifications();
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const normalized = dateString.length === 10 ? dateString + 'T00:00:00' : dateString;
      return format(new Date(normalized), 'MMM do, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const getDaysUntilClose = (closingDate) => {
    if (!closingDate) return null;
    try {
      return Math.ceil((new Date(closingDate) - new Date()) / (1000 * 60 * 60 * 24));
    } catch {
      return null;
    }
  };

  const closingDate =
    tender.closing_date || tender.closingDate || tender.deadline ||
    tender.end_date || tender.endDate || tender.close_date ||
    tender.closeDate || tender.bid_deadline || tender.bidDeadline || null;

  const daysUntilClose = getDaysUntilClose(closingDate);
  const getStatusInfo = () => {
    const esStatus = tender.status?.toLowerCase();
    if (daysUntilClose !== null && daysUntilClose <= 0) {
      if (esStatus === 'active') return { text: 'Closing Date Passed', class: 'status-date-passed' };
      return { text: 'Closed', class: 'status-closed' };
    }
    if (daysUntilClose !== null && daysUntilClose <= 3) return { text: 'Closing Soon', class: 'status-closing-soon' };
    if (daysUntilClose !== null) return { text: 'Open', class: 'status-open' };
    return { text: tender.status || 'Open', class: 'status-open' };
  };

  const statusInfo = getStatusInfo();

  const organizationName =
    tender.organization?.contracting_entity || tender.organization?.name ||
    tender.contracting_entity || tender.buyer_name || tender.department ||
    tender.organization_name ||
    (typeof tender.organization === 'string' ? tender.organization : null) ||
    'Government of Canada';

  const location = `${tender.location?.city || ''}, ${tender.location?.province || ''}`.replace(', ,', '').replace(/^, |, $/, '') || 'Canada';

  const getKeyIndicators = (tender) => {
    const indicators = [];
    if (tender['canadianMade-fabriquéCanadien'] === 'yes' || tender['canadianMade-fabriquéCanadien'] === 'true') {
      indicators.push({ text: 'Canadian Made', type: 'canadian' });
    }
    if (tender['indigenousBusinessRequired-entrepriseAutochtooneRequise'] === 'true') {
      indicators.push({ text: 'Indigenous Preferred', type: 'indigenous' });
    }
    if (tender['experienceRequired-expérienceRequise'] === 'true') {
      indicators.push({ text: 'References Required', type: 'experience' });
    }
    if (tender.securityClearanceRequired === 'true' || tender.security_clearance_required === 'true') {
      indicators.push({ text: 'Security Clearance', type: 'security' });
    }
    const category = tender.procurementCategory || tender['masterCategory-catégoriePrincipale'] || tender.procurement_category || '';
    if (indicators.length === 0) {
      if (category) {
        indicators.push({ text: category, type: 'category' });
      } else {
        indicators.push({ text: 'Open Competition', type: 'general' });
        indicators.push({ text: 'Standard Terms', type: 'general' });
      }
    }
    return indicators;
  };

  const getPriceEstimation = (tender) => {
    const title = tender.title?.toLowerCase() || '';
    const tenderId = tender._id || tender.tender_id || tender.reference_number || '';
    let hash = 0;
    for (let i = 0; i < tenderId.length; i++) {
      hash = ((hash << 5) - hash) + tenderId.charCodeAt(i);
      hash = hash & hash;
    }
    const normalizedHash = Math.abs(hash % 10000) / 10000;
    let baseAmount = 250000;
    let range = 0;
    if (title.includes('infrastructure') || title.includes('construction')) { baseAmount = 2000000; range = 7000000; }
    else if (title.includes('software') || title.includes('it') || title.includes('digital')) { baseAmount = 1000000; range = 4000000; }
    else if (title.includes('consulting') || title.includes('advisory')) { baseAmount = 500000; range = 2000000; }
    else if (title.includes('maintenance') || title.includes('support')) { baseAmount = 300000; range = 1500000; }
    else if (title.includes('security') || title.includes('cybersecurity')) { baseAmount = 800000; range = 3000000; }
    else if (title.includes('research') || title.includes('laboratory')) { baseAmount = 1200000; range = 2500000; }
    else { baseAmount = 400000; range = 1800000; }
    return Math.floor(baseAmount + (normalizedHash * range));
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'TBD';
    return new Intl.NumberFormat('en-CA', {
      style: 'currency', currency: 'CAD',
      minimumFractionDigits: 1, maximumFractionDigits: 1, notation: 'compact'
    }).format(amount);
  };

  const estimatedAmount = getPriceEstimation(tender);
  const keyIndicators = getKeyIndicators(tender);
  const referenceNumber = tender._id || tender.tender_id || tender.reference_number || 'N/A';
  const tenderId = tender._id || tender.tender_id || tender.reference_number;
  const isFrenchTender = !(tender['aiDescription-descriptionIA-eng'] || tender.business_intelligence?.ai_description) &&
    !!(tender.solicitation_number || tender._id || '').toLowerCase().startsWith('qc');

  const handleSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSaved(tenderId)) {
      unsaveTender(tenderId);
      removeBookmark(tenderId);
    } else {
      saveTender(tender);
      addBookmark(tender);
    }
  };

  const handleDisqualify = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (isDisqualified(tenderId)) {
        await undisqualify(tenderId);
      } else {
        if (isBookmarked(tenderId)) removeBookmark(tenderId);
        await disqualify({
          tender_id: tenderId,
          title: tender.title,
          organization: organizationName,
          closing_date: closingDate,
        });
      }
    } catch (err) {
      console.error('disqualify toggle failed', err);
    }
  };

  const handleCardClick = (e) => {
    if (selectionMode && onToggleSelect) {
      e.preventDefault();
      e.stopPropagation();
      onToggleSelect(tenderId);
    }
  };

  return (
    <div className={`tender-card-wrapper ${isSaved(tenderId) ? 'status-saved' : ''} ${selectionMode ? 'selection-mode' : ''} ${selected ? 'selected' : ''}`}>
      {selectionMode && (
        <input
          type="checkbox"
          className="bulk-select-checkbox"
          checked={selected}
          onChange={(e) => { e.stopPropagation(); onToggleSelect && onToggleSelect(tenderId); }}
          onClick={(e) => e.stopPropagation()}
        />
      )}
      <Link
        to={selectionMode ? '#' : `/tender/${encodeURIComponent(tender._id || tender.tender_id || tender.reference_number)}`}
        className="tender-card"
        onClick={handleCardClick}
      >
        <div className="tender-card-header">
          <div className="header-left">
            <span className={`tender-status ${statusInfo.class}`}>{statusInfo.text}</span>
            <h3 className="tender-title">
              {tender.english_title || tender.title || 'Untitled Tender'}
              {isFrenchTender && !tender.english_title && <span className="lang-badge-fr">FR</span>}
            </h3>
          </div>
          <div className="header-actions">
            <button className={`action-btn save-btn ${isSaved(tenderId) ? 'active' : ''}`} onClick={handleSave} title="Save this tender">
              <Star size={12} fill={isSaved(tenderId) ? 'currentColor' : 'none'} />
              Save
            </button>
            <button
              className={`action-btn not-applicable-btn ${isDisqualified(tenderId) ? 'active' : ''}`}
              onClick={handleDisqualify}
              title={isDisqualified(tenderId) ? 'Restore from disqualified' : 'Disqualify (hide from team search)'}
            >
              <Ban size={12} />
              {isDisqualified(tenderId) ? 'Restore' : 'Disqualify'}
            </button>
          </div>
        </div>

        {canadianContentFlag && (
          <div className="canadian-content-warning">
            ⚠ Canadian content required — verify sourcing eligibility before bidding
          </div>
        )}
        <div className="tender-highlights">
          <p className="highlight-field"><strong>Amount:</strong> {formatCurrency(estimatedAmount)} CAD</p>
          <p className="highlight-field">
            <strong>Published:</strong>{' '}
            {formatDate(tender.publish_date || tender.publication_date || tender.publicationDate || tender.date)}
          </p>
          <p className="highlight-field"><strong>Closing Date:</strong> {formatDate(closingDate)}</p>
        </div>

        <div className="tender-details-grid">
          <div className="tender-left-column">
            <p><strong>Category:</strong> {tender.procurementCategory || tender['masterCategory-catégoriePrincipale'] || tender.procurement_category || 'Unknown'}</p>
            <p><strong>Key Indicators:</strong>
              <span className="indicators-inline">
                {keyIndicators.map((indicator, index) => (
                  <span key={index} className="indicator-reference">{indicator.text}</span>
                ))}
              </span>
            </p>
          </div>
          <div className="tender-right-column">
            <p><strong>Organization:</strong> {organizationName}</p>
            <p><strong>Reference:</strong> <span className="reference-number">{referenceNumber}</span></p>
          </div>
        </div>
      </Link>
    </div>
  );
};

const Pagination = ({ currentPage, totalItems, itemsPerPage, onPageChange, filteredCount }) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  const [jumpInput, setJumpInput] = useState('');
  const submitJump = (e) => {
    if (e) e.preventDefault();
    const n = parseInt(jumpInput, 10);
    if (Number.isFinite(n)) {
      onPageChange(Math.min(Math.max(1, n), totalPages));
      setJumpInput('');
    }
  };

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }
    if (currentPage - delta > 2) { rangeWithDots.push(1, '...'); } else { rangeWithDots.push(1); }
    rangeWithDots.push(...range);
    if (currentPage + delta < totalPages - 1) { rangeWithDots.push('...', totalPages); } else if (totalPages > 1) { rangeWithDots.push(totalPages); }
    return rangeWithDots;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="pagination">
      <div className="pagination-info">
        {filteredCount} recommended tender{filteredCount !== 1 ? 's' : ''} on this page &middot; page {currentPage} of {totalPages}
      </div>
      <div className="pagination-controls">
        <button className="pagination-btn" onClick={() => onPageChange(1)} disabled={currentPage === 1} title="First page">«</button>
        <button className="pagination-btn" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
          <ChevronLeft size={16} />Previous
        </button>
        <div className="pagination-numbers">
          {getVisiblePages().map((page, index) => (
            <button
              key={index}
              className={`pagination-number ${page === currentPage ? 'active' : ''} ${page === '...' ? 'dots' : ''}`}
              onClick={() => typeof page === 'number' && onPageChange(page)}
              disabled={page === '...'}
            >
              {page}
            </button>
          ))}
        </div>
        <button className="pagination-btn" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
          Next<ChevronRight size={16} />
        </button>
        <button className="pagination-btn" onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages} title="Last page">»</button>
        <form className="pagination-jump" onSubmit={submitJump}>
          <label htmlFor="rec-page-jump">Go to</label>
          <input
            id="rec-page-jump"
            type="number" min={1} max={totalPages}
            placeholder={String(currentPage)}
            value={jumpInput}
            onChange={(e) => setJumpInput(e.target.value)}
            onBlur={() => jumpInput && submitJump()}
            aria-label={`Go to page (1-${totalPages})`}
          />
          <span className="pagination-jump-of">/ {totalPages.toLocaleString()}</span>
        </form>
      </div>
    </div>
  );
};

export default Recommended;
